// ─── Feuille : créer / éditer un POSTE budgétaire ─────────────────
// Nom, emoji, répartition des parts (%) entre les membres de l'espace, et accès.
// Les PARTS sont du métier (qui doit combien) — distinctes de l'ACCÈS (qui voit
// le poste), géré par SectionAcces. Un membre à 0 % n'est pas un payeur.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { Feuille, SectionAcces, stylesForm, useUI, useT } from '@mop/core';
import { EMOJIS_BUDGET, emojiBudget } from './data/categories';
import { totalPourcentages } from './services/calculs';

const VIDE = { nom: '', emoji: '💰', pcts: {}, accesN: {} };

export default function FeuilleBudget({ visible, initial, membres = [], ownerUid, onEnregistrer, onFermer }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [f, setF] = useState(VIDE);

  useEffect(() => {
    if (!visible) return;
    const b = initial || {};
    const pcts = {};
    (b.payeurs || []).forEach(p => { if (p.uid) pcts[p.uid] = String(p.pct ?? ''); });
    setF({ nom: b.nom || '', emoji: emojiBudget(b), pcts, accesN: b.accesN || {} });
  }, [visible, initial]);

  const set = (k, v) => setF(x => ({ ...x, [k]: v }));
  const setPct = (uid, v) => setF(x => ({ ...x, pcts: { ...x.pcts, [uid]: v.replace(/[^0-9.,]/g, '') } }));

  const payeurs = membres
    .map(m => ({ uid: m.uid, nom: m.nom, pct: parseFloat(String(f.pcts[m.uid] || '').replace(',', '.')) || 0 }))
    .filter(p => p.pct > 0);
  const total = totalPourcentages(payeurs);
  // Une répartition incohérente n'est pas bloquante (on peut vouloir 0 %
  // partout au départ) mais elle est SIGNALÉE : sans 100 %, les parts dues
  // ne couvrent pas la dépense et le « qui doit à qui » serait faux.
  const alerteTotal = payeurs.length > 0 && Math.abs(total - 100) > 0.005;
  const modifie = !!(f.nom || '').trim();

  return (
    <Feuille visible={visible} titre={initial?.id ? t('budget.modifierBudget') : t('budget.nouveauBudget')}
      emoji={f.emoji} hauteur={0.85} onFermer={onFermer}
      action={{ icone: initial?.id ? '💾' : '＋', label: t('commun.enregistrer'), disabled: !modifie,
        onPress: () => onEnregistrer({ nom: f.nom, emoji: f.emoji, payeurs, accesN: f.accesN }) }}>

      <Text style={s.lbl}>{t('commun.nom')}</Text>
      <TextInput style={s.input} value={f.nom} onChangeText={v => set('nom', v)}
        placeholder={t('budget.nouveauBudget')} placeholderTextColor={UI.faint} />

      <Text style={s.lbl}>{t('commun.emoji')}</Text>
      <View style={s.emojis}>
        {EMOJIS_BUDGET.map(e => (
          <TouchableOpacity key={e} style={[s.emoji, f.emoji === e && s.emojiSel]} activeOpacity={0.7}
            onPress={() => set('emoji', e)}>
            <Text style={{ fontSize: 20 }}>{e}</Text>
          </TouchableOpacity>
        ))}
      </View>

      <Text style={s.lbl}>{t('budget.payeurs')}</Text>
      <Text style={s.aide}>{t('budget.payeursAide')}</Text>
      {membres.length === 0 ? (
        <Text style={s.vide}>{t('budget.aucunMembreDispo')}</Text>
      ) : membres.map(m => (
        <View key={m.uid} style={s.ligne}>
          <Text style={s.nom} numberOfLines={1}>{m.nom}</Text>
          <TextInput style={s.pct} value={f.pcts[m.uid] || ''} onChangeText={v => setPct(m.uid, v)}
            keyboardType="numeric" placeholder="0" placeholderTextColor={UI.faint} />
          <Text style={s.pctSigne}>%</Text>
        </View>
      ))}
      {payeurs.length > 0 && (
        <Text style={[s.total, alerteTotal && { color: UI.danger || '#c0392b' }]}>
          {alerteTotal ? t('budget.pctWarn', { total }) : t('budget.pctOk', { total })}
        </Text>
      )}

      <SectionAcces titre={t('acces.titre')} emoji={f.emoji} acces={f.accesN}
        membres={membres} ownerUid={ownerUid} onChange={(a) => set('accesN', a)} />
    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  ...stylesForm(UI),
  emojis: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  emoji: { width: 44, height: 44, borderRadius: 11, alignItems: 'center', justifyContent: 'center', backgroundColor: UI.card, borderWidth: 0.5, borderColor: UI.border },
  emojiSel: { borderWidth: 2, borderColor: UI.accent },
  aide: { fontSize: 12, color: UI.faint, lineHeight: 17, marginBottom: 6 },
  ligne: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 7 },
  nom: { flex: 1, fontSize: 14.5, color: UI.ink },
  pct: { width: 70, textAlign: 'right', fontSize: 15, color: UI.ink, backgroundColor: UI.card, borderRadius: 10, borderWidth: 0.5, borderColor: UI.border, paddingVertical: 9, paddingHorizontal: 10 },
  pctSigne: { fontSize: 14, color: UI.muted, width: 16 },
  total: { fontSize: 13, fontWeight: '800', color: UI.muted, marginTop: 8 },
  vide: { fontSize: 13, color: UI.faint, lineHeight: 18, paddingVertical: 8 },
});
